import {
  BedDouble,
  Bike,
  CircleCheck,
  Inbox,
  PauseCircle,
  RefreshCw,
  RotateCw,
  ShoppingBag,
  Store,
  type LucideIcon,
} from 'lucide-react';

import { isBlocked, isOnShift, type DeliveryJob, type RiderProfile } from '@/api/types';
import { AlertsCard } from '@/components/alerts-card';
import { Main, ShiftHeader } from '@/components/app-shell';
import { EmptyState, ErrorState } from '@/components/states';
import { JobCard, JobCardSkeleton } from '@/components/job-card';
import { Button } from '@/components/ui/button';
import { LoadingAnnouncer } from '@/components/ui/skeleton';
import { useRiderSession } from '@/auth/rider-session';
import { useRunSheet } from '@/hooks/use-deliveries';
import { plural } from '@/lib/format';
import { planRunSheet, type ShopStop } from '@/lib/route';
import { cn } from '@/lib/utils';

/**
 * The run sheet: everything this rider is carrying that has not finished.
 *
 * Split by what happens next — deliver, collect, accept — and worked top to
 * bottom, the job at the very top drawn large. A dispatch board sorts
 * newest-first; this is not a dispatch board.
 */
export function JobsScreen({ rider }: { rider: RiderProfile }) {
  const { jobs, isPending, isError, error, refetch, isFetching } = useRunSheet();

  return (
    <>
      <ShiftHeader rider={rider} />
      <Main className="pt-4">
        {/* Above the list, so it is seen on an empty sheet too — which is
            exactly when a rider is waiting for the next job to arrive. */}
        <AlertsCard className="mb-4" />
        <RunSheet
          rider={rider}
          jobs={jobs}
          isPending={isPending}
          isError={isError}
          error={error}
          isFetching={isFetching}
          onRefresh={() => void refetch()}
        />
      </Main>
    </>
  );
}

function RunSheet({
  rider,
  jobs,
  isPending,
  isError,
  error,
  isFetching,
  onRefresh,
}: {
  rider: RiderProfile;
  jobs: ReturnType<typeof useRunSheet>['jobs'];
  isPending: boolean;
  isError: boolean;
  error: unknown;
  isFetching: boolean;
  onRefresh: () => void;
}) {
  // Only a FIRST load blocks the screen. A refetch keeps the sheet on screen
  // underneath, because a rider halfway through a delivery should not lose
  // sight of it to a poll that happened to fail in a lift.
  if (isPending) {
    return (
      <div className="grid gap-3">
        <LoadingAnnouncer label="Loading your jobs" />
        <JobCardSkeleton lead />
        <JobCardSkeleton />
        <JobCardSkeleton />
      </div>
    );
  }

  if (isError && jobs.length === 0) {
    return <ErrorState error={error} onRetry={onRefresh} />;
  }

  if (jobs.length === 0) {
    return (
      <div className="flex min-h-[60vh] flex-col">
        <EmptySheet rider={rider} onRefresh={onRefresh} busy={isFetching} />
      </div>
    );
  }

  return (
    <>
      <PlannedSheet jobs={jobs} />

      {/* The sheet ends on purpose, and says how it refills itself. */}
      <footer className="mt-6 flex flex-col items-center gap-3 border-t border-line pt-5">
        <p className="flex items-center gap-2 text-center text-sm text-ink-subtle">
          <RefreshCw className="size-3.5 shrink-0" aria-hidden="true" />
          This list checks itself every half minute
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          loading={isFetching}
          loadingLabel="Checking"
        >
          <RotateCw aria-hidden="true" />
          Check now
        </Button>
      </footer>
    </>
  );
}

/**
 * The run sheet as three piles a rider can read without knowing the app.
 *
 * Several orders at once is where people who are not at ease with phones get
 * lost: one long list mixes bags already in hand with shops still to visit and
 * jobs nobody has accepted. So the sheet is split by what the rider DOES next —
 * deliver, collect, accept — in that order, because a bag in hand always
 * comes first. Orders at the same shop are grouped under it, since three
 * orders at one counter are one stop.
 *
 * The first card on the sheet is the lead: the one thing to do now.
 */
function PlannedSheet({ jobs }: { jobs: DeliveryJob[] }) {
  const { ping } = useRiderSession();
  const plan = planRunSheet(jobs, ping.position);
  const leadId = plan.deliver[0]?.job.id ?? plan.collect[0]?.jobs[0]?.id ?? plan.accept[0]?.id;

  return (
    <div className="grid gap-6">
      <WorkSummary
        deliver={plan.deliver.length}
        collect={plan.collect.reduce((count, stop) => count + stop.jobs.length, 0)}
        accept={plan.accept.length}
      />

      {plan.deliver.length > 0 ? (
        <SheetSection
          id="deliver"
          icon={Bike}
          title="Deliver these"
          note={
            plan.ordered
              ? 'Nearest door first, as the crow flies from where you are. You know the roads better than we do.'
              : undefined
          }
        >
          <ol className="grid gap-3">
            {plan.deliver.map((entry, index) => (
              <li key={entry.job.id}>
                <JobCard
                  job={entry.job}
                  lead={entry.job.id === leadId}
                  stopNumber={plan.deliver.length > 1 ? index + 1 : undefined}
                />
              </li>
            ))}
          </ol>
        </SheetSection>
      ) : null}

      {plan.collect.length > 0 ? (
        <SheetSection id="collect" icon={Store} title="Collect from the shop">
          <div className="grid gap-5">
            {plan.collect.map((stop) => (
              <ShopGroup key={stop.pharmacyId} stop={stop} leadId={leadId} />
            ))}
          </div>
        </SheetSection>
      ) : null}

      {plan.accept.length > 0 ? (
        <SheetSection
          id="accept"
          icon={Inbox}
          title="New jobs"
          note="Open each one and accept it, so the office knows you have it."
        >
          <ul className="grid gap-3">
            {plan.accept.map((job) => (
              <li key={job.id}>
                <JobCard job={job} lead={job.id === leadId} />
              </li>
            ))}
          </ul>
        </SheetSection>
      ) : null}
    </div>
  );
}

/**
 * Three counts across the top — the whole shift at a glance.
 *
 * Each is a link to its pile, and a pile with nothing in it is drawn quiet
 * rather than hidden, so the three are always in the same place.
 */
function WorkSummary({
  deliver,
  collect,
  accept,
}: {
  deliver: number;
  collect: number;
  accept: number;
}) {
  const items = [
    { id: 'deliver', label: 'To deliver', count: deliver },
    { id: 'collect', label: 'To collect', count: collect },
    { id: 'accept', label: 'New', count: accept },
  ];

  return (
    <nav aria-label="Your work right now" className="grid grid-cols-3 gap-2">
      {items.map((item) =>
        item.count > 0 ? (
          <a
            key={item.id}
            href={`#${item.id}`}
            className={cn(
              'grid gap-0.5 rounded-xl border border-line bg-raised px-3 py-2.5',
              'transition-[border-color] duration-[var(--motion-duration-fast)] ease-standard',
              'hover:border-line-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
            )}
          >
            <span data-numeric className="text-2xl font-bold leading-none text-ink">
              {item.count}
            </span>
            <span className="text-xs font-medium text-ink-muted">{item.label}</span>
          </a>
        ) : (
          <div
            key={item.id}
            className="grid gap-0.5 rounded-xl border border-dashed border-line bg-sunken/60 px-3 py-2.5"
          >
            <span data-numeric className="text-2xl font-bold leading-none text-ink-subtle">
              0
            </span>
            <span className="text-xs font-medium text-ink-subtle">{item.label}</span>
          </div>
        ),
      )}
    </nav>
  );
}

function SheetSection({
  id,
  icon: Icon,
  title,
  note,
  children,
}: {
  id: string;
  icon: LucideIcon;
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} aria-labelledby={`${id}-heading`} className="grid gap-3 scroll-mt-24">
      <div className="grid gap-1">
        <h2
          id={`${id}-heading`}
          className="flex items-center gap-2 text-lg font-bold tracking-tight text-ink"
        >
          <Icon className="size-5 shrink-0 text-brand" aria-hidden="true" />
          {title}
        </h2>
        {note ? <p className="text-sm leading-snug text-ink-subtle">{note}</p> : null}
      </div>
      {children}
    </section>
  );
}

/** One shop and every order waiting at it. */
function ShopGroup({ stop, leadId }: { stop: ShopStop; leadId: string | undefined }) {
  const many = stop.jobs.length > 1;

  return (
    <div className="grid gap-2">
      <div className="flex items-baseline justify-between gap-3 px-1">
        <h3 className="min-w-0 font-semibold text-ink [overflow-wrap:anywhere]">
          {stop.pharmacyName}
        </h3>
        <span data-numeric className="shrink-0 text-sm text-ink-muted">
          {plural(stop.jobs.length, 'order')}
          {stop.ready > 0 ? ` · ${stop.ready} ready` : ''}
        </span>
      </div>

      {/*
       * The batch pickup lives on the delivery screen, at the counter. Said
       * here so a rider with three bags waiting knows they will not be doing
       * this three times.
       */}
      {many && stop.ready > 1 ? (
        <p className="flex items-start gap-2 rounded-lg bg-wash px-3 py-2 text-sm leading-snug text-brand">
          <ShoppingBag className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          At the counter, open any one of these to collect all {stop.ready} together.
        </p>
      ) : null}

      <ul className="grid gap-3">
        {stop.jobs.map((job) => (
          <li key={job.id}>
            <JobCard job={job} lead={job.id === leadId} hideShop />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Nothing to do — which means one of three quite different things. */
function EmptySheet({
  rider,
  onRefresh,
  busy,
}: {
  rider: RiderProfile;
  onRefresh: () => void;
  busy: boolean;
}) {
  if (isBlocked(rider)) {
    return (
      <EmptyState
        icon={PauseCircle}
        title="Your account is paused"
        description="The office has to lift this. Nothing will be assigned to you until they do."
      />
    );
  }

  if (!isOnShift(rider)) {
    return (
      <EmptyState
        icon={BedDouble}
        title="You are off shift"
        // The switch is in the bar directly above this, so it is pointed at
        // rather than repeated — two switches for one state is how they end
        // up disagreeing.
        description="Turn the switch at the top on, and the office can start sending you jobs."
      />
    );
  }

  return (
    <EmptyState
      icon={CircleCheck}
      title="Nothing waiting"
      description="You are on shift. New jobs land here on their own."
      action={
        <Button variant="secondary" onClick={onRefresh} loading={busy} loadingLabel="Checking">
          <RotateCw aria-hidden="true" />
          Check now
        </Button>
      }
    />
  );
}
